import React, { useMemo } from 'react';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { PolyglotComponentProps, getPolyglot } from '../../../i18n';
import { Group } from '../../../redux/groups/api/group.model';
import { RouterComponentProps } from '../../../util/route-dom';
import type { NavQueryParams } from '../Device/DeviceOverview/DeviceLayout';
import { getGroupByGroupIdSelector } from '../../../redux/groups/selectors';
import { useAppSelector } from '../../../redux/store.model';
import { useGroupById } from '../../../redux/groups/hooks/useGroupById';
import { Skeleton } from '@mui/material';
import { BreadcrumbSection } from '../../../theme/components/Breadcrumb/Breadcrumb';
import IncidentTitle from '../../Incident/IncidentTitle';

export interface NavigationPathProps
  extends RouterComponentProps,
    PolyglotComponentProps {}

export interface NavigationPath extends BreadcrumbSection {}

const patterns = ['/devicemanager/group/:groupId/:moduleSelected/*'];

/**
 * Get path after control-unit/:controlUnitId/:moduleSelected/*
 * @param pathname
 * @returns string moduleSelected
 *
 * // TODO maybe is better use another patterns of ReactRouter to be available to use useMatches
 *
 * devicemanager/device/96864e27-e43d-48bf-bb20-f1b72a8772f0/group/04d6ce1b-117d-4a53-90bc-e7034369ad50/device-detail/control-unit/1020/alarms/d84c4e77-49ed-4108-9982-f56186c28a5a ==> alarms
 */
export const getParamsGenerator = (innerPatterns: string[]) =>
  function getParams(pathname: string) {
    const match = innerPatterns
      .map((pattern) => matchPath({ path: pattern }, pathname))
      .find(Boolean);

    return match?.params ?? { moduleSelected: undefined };
  };
export const getParamsGroup = getParamsGenerator(patterns);
export const getModuleSelectedGroup = (pathname: string) => {
  const params = getParamsGroup(pathname);
  const lastValue =
    params['*'] && params['*']?.length > 0
      ? params['*']
      : params.moduleSelected;
  return lastValue ?? '';
};

const dicTitles: Record<string, string> = {
  incidents: 'incident_manager.title',
  recipes: 'recipe_manager.title',
  history: 'device.monitoring.title',
  settings: 'group.tooltip.open_window_edit',
  alarms: 'incident.all_alarms',
  reports: 'reports.reports',
  map: 'map.title',
};
/**
 * Navigation path
 * Build the navigation path used in the breadcrumb
 * @param props {NavigationPathProps} need router-dom and polyglot
 * @param string groupId
 * @param array groups
 * @param object group
 * @return array sectionsNavigation
 */
export function navigationPath(
  props: Pick<NavQueryParams, 'navigate' | 'polyglot'>,
  groupId: string | undefined,
  group: Group | undefined,
  pathname: string = window.location.pathname
): NavigationPath[] {
  const { polyglot, navigate } = props;
  const moduleSelected = getModuleSelectedGroup(pathname);

  const sectionsNavigation: NavigationPath[] = [
    {
      content: polyglot.t('nav.menu.devicemanager'),
      onClick: () => navigate('/devicemanager'),
      href: '/devicemanager',
      key: 'devicemanager',
    },
  ];

  /* build path */
  if (groupId && group !== undefined) {
    const pathToGroup = group.pathGroupsIds;
    pathToGroup.forEach((gId) => {
      sectionsNavigation.push({
        content: <GroupPathContent groupId={gId} />,
        onClick: () => navigate(`/devicemanager/group/${gId}`),
        href: `/devicemanager/group/${gId}`,
        key: `group-${gId}`,
      });
    });
  }

  /** module selected */
  if (moduleSelected && Object.keys(dicTitles).includes(moduleSelected ?? '')) {
    sectionsNavigation.push({
      content: polyglot.t(dicTitles[moduleSelected] ?? ''),
      onClick: () =>
        navigate(`/devicemanager/group/${groupId}/${moduleSelected}`),
      href: `/devicemanager/group/${groupId}/${moduleSelected}`,
      key: moduleSelected,
    });
  }
  const subModules = inferSubModuleSelected(getParamsGroup(pathname));
  sectionsNavigation.push(...subModules);
  return sectionsNavigation;
}

export const inferSubModuleSelected = (
  params: ReturnType<typeof getParamsGroup>
): NavigationPath[] => {
  if (params.moduleSelected) {
    if (params.moduleSelected === 'alarms' && params['*']) {
      return [
        {
          content: <IncidentTitle alarmId={params['*']} />,
          href: `/devicemanager/group/${params.groupId}/${params.moduleSelected}/${params['*']}`,
          key: `alarm-${params['*']}`,
        },
      ];
    }
  }

  return [];
};

const GroupPathContent = ({ groupId }: { groupId: string | undefined }) => {
  const { data: group, isLoading } = useGroupById(groupId);

  if (isLoading) return <Skeleton variant="text" width={64} />;
  if (!group) return null;
  return <span>{group.name}</span>;
};

export function useNavigationGroupPath() {
  const params = useParams<{ groupId: string }>();
  const group = useAppSelector((state) =>
    getGroupByGroupIdSelector(state, params)
  );
  const location = useLocation();
  const navigate = useNavigate();
  const polyglot = getPolyglot();
  const groupId = params.groupId;
  const sectionsNavigation = navigationPath(
    { navigate, polyglot },
    groupId,
    group,
    location.pathname
  );
  return sectionsNavigation;
}
